import { zodResolver } from "@hookform/resolvers/zod";
import { useParams } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useGetMyAccount, useGetMyProfile, useGetSetlist, useGetSetlists } from "@/api/queries";
import useAppStore from "@/store/appStateZustand";

import type { CoverItem } from "../types/setlist.types";
import type { SetlistFormData } from "../utils/setlistCreateUpdateSchema";

import { createSetlistSchema } from "../utils/setlistCreateUpdateSchema";
import { parseLegacyStoredSongs } from "../utils/setlistCreateUpdateUtils";
import useWorkSearch from "./useWorkSearch";

export function useSetlistCreateUpdateForm() {
  const [pending, setPending] = useState(false);
  const [mutationErrorMessage, setMutationErrorMessage] = useState("");

  const { t } = useTranslation();

  const { id } = useParams({
    strict: false,
    select: (p): { id: string | undefined } => ({ id: (p as Record<string, string | undefined>).id }),
  });

  const isUpdate = Boolean(id);

  const selectedAccount = useAppStore(s => s.selectedAccount) as string;
  const myAccount = useGetMyAccount({ artistaccountcode: selectedAccount });
  const profile = useGetMyProfile();
  const setlist = useGetSetlist({ ac: selectedAccount, id: id ?? "" });
  const setlists = useGetSetlists({
    ac: selectedAccount,
    oc: myAccount.data?.originatorcode?.code,
  });

  const { options, cachedSongs, noOptionsMessage, handleInputChange } = useWorkSearch(selectedAccount);
  const form = useForm<SetlistFormData>({
    resolver: zodResolver(createSetlistSchema(t)),
    defaultValues: {
      name: "",
      notes: "",
      songList: [],
      coverSongs: [],
    },
  });

  // Guard re-running form
  const initializedRef = useRef<string | null>(null);

  // legacy stored songs guards against legacy type of setlist data.
  // This is because legacy setlists can show with no label in edit form flow
  useEffect(() => {
    if (!isUpdate || !setlist.isSuccess || setlist.data == null || id == null)
      return;

    const key = `${id}__${setlist.dataUpdatedAt}`;
    const isFirstLoad = initializedRef.current !== key;

    const songList: SetlistFormData["songList"] = setlist.data[0].songs != null
      ? parseLegacyStoredSongs(JSON.parse(setlist.data[0].songs)).map((s) => {
          const workcode = s.workCode ?? s.workcode ?? "";
          const storedTitle = s.Title ?? s.title ?? "";
          const artist = s.performingartists ?? "";
          return {
            workcode,
            title: storedTitle,
            performingartists: artist,
            label: storedTitle
              ? artist ? `${storedTitle} - (${artist})` : storedTitle
              : workcode,
          };
        })
      : [];

    if (isFirstLoad) {
      initializedRef.current = key;
      form.reset({
        name: setlist.data[0].name,
        notes: setlist.data[0].notes ?? "",
        songList,
        coverSongs: setlist.data[0].other_songs != null
          ? (JSON.parse(setlist.data[0].other_songs) as CoverItem[])
          : [],
      });
    }
    else {
      form.setValue("songList", songList, { shouldDirty: false });
    }
  }, [isUpdate, setlist.isSuccess, setlist.dataUpdatedAt, id, form, setlist.data]);

  return {
    form,
    id,
    isUpdate,
    selectedAccount,
    myAccount,
    profile,
    setlist,
    setlists,
    pending,
    setPending,
    mutationErrorMessage,
    setMutationErrorMessage,
    options,
    cachedSongs,
    noOptionsMessage,
    handleInputChange,
  };
}
